import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Crown, Star, Award, Heart, GraduationCap, Shield, Anchor,
  CalendarCheck, Repeat, HandCoins, Wrench,
} from "lucide-react";

const tiers = [
  {
    key: "platinum", label: "Platinum Kraken", range: "$1,500+",
    icon: Crown, color: "#00E5FF",
    benefits: [
      "Largest logo on sponsor banner",
      "Logo and business link on website",
      "2 dedicated sponsor spotlight posts per year",
      "Featured in team emails and announcements",
      "Recognition at team events and fundraisers",
      "Sponsor plaque presented by team",
    ],
  },
  {
    key: "gold", label: "Gold Kraken", range: "$750–$1,499",
    icon: Star, color: "#FFD700",
    benefits: [
      "Large logo on sponsor banner",
      "Logo and business link on website",
      "1 dedicated sponsor spotlight post",
      "Recognition at team events",
      "Sponsor plaque",
    ],
  },
  {
    key: "silver", label: "Silver Kraken", range: "$300–$749",
    icon: Award, color: "#C0C0C0",
    benefits: [
      "Small logo on sponsor banner",
      "Website recognition",
      "Social media thank-you post",
    ],
  },
  {
    key: "friends", label: "Friends of Coastal Kraken", range: "$100–$299",
    icon: Heart, color: "#94A3B8",
    benefits: [
      "Name listed on website",
      "Group social media thank-you post",
    ],
  },
];

const monthly = [
  {
    key: "supporter", label: "Kraken Supporter", range: "$25 / month",
    icon: Heart, color: "#94A3B8",
    benefits: [
      "Name listed on website",
      "Group social media thank-you post",
    ],
  },
  {
    key: "partner", label: "Kraken Partner", range: "$50 / month",
    icon: Repeat, color: "#7FDBFF",
    benefits: [
      "Logo or name listed on website",
      "Group social media thank-you post",
      "Recognition as a Monthly Community Partner",
    ],
  },
  {
    key: "champion", label: "Kraken Champion", range: "$100 / month",
    icon: Crown, color: "#00E5FF",
    benefits: [
      "Logo and business link on website",
      "Dedicated social media thank-you post",
      "Recognition as a Monthly Community Partner",
      "Small logo on sponsor banner",
    ],
  },
];

const directImpact = [
  {
    key: "scholarship", label: "Player Scholarship Sponsor", range: "$500",
    icon: GraduationCap, color: "#00E5FF",
    description: "Helps cover costs for a local player.",
    benefits: [
      "Website recognition",
      "Dedicated social media thank-you post",
      "Recognition as a Player Scholarship Sponsor",
    ],
  },
  {
    key: "tournament", label: "Tournament Sponsor", range: "$750",
    icon: CalendarCheck, color: "#FFD700",
    description: "Helps cover tournament entry fees.",
    benefits: [
      "Website logo and business link",
      "Dedicated social media thank-you post before tournament weekend",
      "Recognition as a Tournament Sponsor",
    ],
  },
  {
    key: "equipment", label: "Equipment Sponsor", range: "$1,000",
    icon: Wrench, color: "#7FDBFF",
    description: "Helps cover baseballs, catcher's gear, training equipment, and team equipment.",
    benefits: [
      "Website logo and business link",
      "Dedicated sponsor spotlight post",
      "Recognition as an Equipment Sponsor",
      "Logo on sponsor banner",
    ],
  },
  {
    key: "veteran", label: "Veteran & First Responder Support", range: "Any amount",
    icon: Shield, color: "#C0C0C0",
    description: "Helps keep costs affordable for local families.",
    benefits: [
      "Name listed on website",
      "Group social media thank-you post",
    ],
  },
];

function TierCard({ t, compact = false }) {
  return (
    <div className="kr-card rounded-xl p-6 flex flex-col" data-testid={`tier-${t.key}`}>
      <t.icon size={compact ? 26 : 32} style={{ color: t.color }} className="mb-3" />
      <div className="font-display text-lg uppercase leading-tight">{t.label}</div>
      <div className="text-[#00E5FF] text-sm mt-1 font-semibold">{t.range}</div>
      {t.description && (
        <p className="text-slate-300 text-sm mt-3 leading-relaxed">{t.description}</p>
      )}
      <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]/80 mt-4 mb-2">You receive</div>
      <ul className="text-slate-300 text-sm space-y-2 flex-1">
        {t.benefits.map((b) => (
          <li key={b} className="flex gap-2">
            <span className="text-[#00E5FF] shrink-0">▸</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [summary, setSummary] = useState({ total: 0, goal: 8000, percent: 0 });
  const [form, setForm] = useState({ name: "", email: "", tier: "gold", amount: "", message: "" });

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
    api.get("/sponsors/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const all = [...tiers, ...monthly, ...directImpact];
      const opt = all.find((t) => t.key === form.tier) || {};
      const tierLabel = opt.label || form.tier;
      const amountSuffix = form.amount ? ` ($${form.amount})` : "";
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        topic: `Sponsor Inquiry — ${tierLabel}${amountSuffix}`,
        message: form.message,
        phone: "",
      });
      toast.success("Inquiry submitted — we'll be in touch!");
      setForm({ name: "", email: "", tier: "gold", amount: "", message: "" });
    } catch {
      toast.error("Failed to submit");
    }
  };

  return (
    <div data-testid="sponsors-page" className="py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="eyebrow mb-3">Partner With Us</div>
        <h1 className="font-display text-5xl sm:text-6xl uppercase font-bold tracking-tight">
          Become A Kraken Sponsor
        </h1>

        {/* Nonprofit / veteran-founded statement */}
        <div
          className="kr-card rounded-xl p-5 sm:p-6 mt-8 flex items-start gap-4 border border-[#00E5FF]/20"
          data-testid="nonprofit-statement"
        >
          <Anchor size={22} className="text-[#00E5FF] mt-1 shrink-0" />
          <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
            Coastal Kraken Baseball is a <span className="text-[#00E5FF] font-semibold">veteran-founded nonprofit organization</span> dedicated
            to developing young athletes through baseball, faith, leadership, character, and service.
          </p>
        </div>

        {/* Goal progress */}
        <div className="kr-card rounded-2xl p-6 sm:p-8 mt-10" data-testid="sponsor-progress">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div>
              <div className="eyebrow mb-1">Season Goal</div>
              <div className="font-display text-3xl sm:text-4xl uppercase">
                ${summary.total.toLocaleString()}{" "}
                <span className="text-slate-500 text-xl sm:text-2xl">/ ${summary.goal.toLocaleString()}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl sm:text-5xl text-[#00E5FF]">{summary.percent}%</div>
              <div className="text-xs uppercase tracking-widest text-slate-400">to goal</div>
            </div>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${summary.percent}%` }} />
          </div>
        </div>

        {/* SECTION 1 — Sponsorship Tiers */}
        <div className="mt-14" data-testid="section-tiers">
          <div className="eyebrow mb-2">Section 1</div>
          <h2 className="font-display text-3xl sm:text-4xl uppercase font-bold tracking-tight mb-2">
            Sponsorship Tiers
          </h2>
          <p className="text-slate-400 text-sm max-w-2xl mb-6">
            Four set sponsorship levels with defined benefits and visibility for your business or family.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((t) => <TierCard key={t.key} t={t} />)}
          </div>
        </div>

        {/* SECTION 2 — Monthly Community Partners */}
        <div className="mt-14" data-testid="section-monthly">
          <div className="eyebrow mb-2">Section 2</div>
          <h2 className="font-display text-3xl sm:text-4xl uppercase font-bold tracking-tight mb-2">
            Monthly Community Partners
          </h2>
          <p className="text-slate-300 text-sm sm:text-base max-w-3xl mb-6 leading-relaxed">
            Monthly Community Partners help Coastal Kraken Baseball keep costs affordable for local families year-round.
            This is a great option for businesses or individuals who want to support the team without making one large donation upfront.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {monthly.map((m) => <TierCard key={m.key} t={m} compact />)}
          </div>
        </div>

        {/* SECTION 3 — Direct Impact Sponsorships */}
        <div className="mt-14" data-testid="section-direct-impact">
          <div className="eyebrow mb-2">Section 3</div>
          <h2 className="font-display text-3xl sm:text-4xl uppercase font-bold tracking-tight mb-2">
            Direct Impact Sponsorships
          </h2>
          <p className="text-slate-400 text-sm max-w-2xl mb-6">
            Targeted giving options that fund specific areas of the program — from players to gear to tournaments.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {directImpact.map((d) => <TierCard key={d.key} t={d} compact />)}
          </div>
        </div>

        {/* Sponsors list + inquiry form */}
        <div className="grid lg:grid-cols-2 gap-8 mt-14">
          <div>
            <div className="eyebrow mb-3">Current Sponsors</div>
            <h3 className="font-display text-3xl uppercase mb-6">Thank You</h3>
            <div className="space-y-3">
              {sponsors.length === 0 ? (
                <div className="kr-card rounded-xl p-8 text-center" data-testid="no-sponsors">
                  <div className="font-display text-2xl uppercase text-[#00E5FF]">Be Our First Sponsor</div>
                  <p className="text-slate-400 mt-3 text-sm">Your logo and recognition will live right here.</p>
                </div>
              ) : sponsors.map((s) => (
                <div key={s.id} className="kr-card rounded-xl p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{s.name}</div>
                    {s.message && <div className="text-slate-400 text-xs truncate">{s.message}</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest border border-white/10 rounded-full px-3 py-1 shrink-0">{s.tier}</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="kr-card rounded-xl p-6 sm:p-8 space-y-4 h-fit" data-testid="sponsor-form">
            <div className="eyebrow flex items-center gap-2"><HandCoins size={14} /> Sponsor Inquiry</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Interested in supporting Coastal Kraken Baseball? Contact us through the sponsor inquiry form and we&apos;ll follow up with sponsorship details.
            </p>
            <input required placeholder="Name / Business"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sponsor-name" />
            <input required type="email" placeholder="Email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sponsor-email" />
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
              className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sponsor-tier">
              <optgroup label="Sponsorship Tiers">
                {tiers.map((t) => <option key={t.key} value={t.key}>{t.label} ({t.range})</option>)}
              </optgroup>
              <optgroup label="Monthly Community Partners">
                {monthly.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.range})</option>)}
              </optgroup>
              <optgroup label="Direct Impact Sponsorships">
                {directImpact.map((d) => <option key={d.key} value={d.key}>{d.label} ({d.range})</option>)}
              </optgroup>
            </select>
            <input type="number" min="0" step="1" placeholder="Amount ($)"
              value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sponsor-amount" />
            <textarea rows={4} placeholder="Message"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sponsor-message" />
            <button className="btn-primary w-full justify-center" data-testid="sponsor-submit">Submit Inquiry</button>
          </form>
        </div>
      </div>
    </div>
  );
}
